import {Component, Host, h, Prop, Method, type JSX} from '@stencil/core';
import {GuidedApiSuggestion} from '../api';
import axios from 'axios';
import {authenticationHeaders} from '../subcomponents/utils';
import { makeLogger } from '../../../logger/logger';
import {highlight, parseJsonWithFallback} from './utils';

const { err } = makeLogger({
  pathToCurrentFile: 'components/design-system/status/cose-suggestions.tsx',
  loggersList: ['err']
});

/**
 * @Deprecated use cosev2 instead
 */
@Component({
  tag: 'uiaas-guided-api-cose-suggestions',
  shadow: true
})
export class GuidedApiCoseSuggestions {

    @Prop() readonly term: string;
    @Prop() readonly api: string;

    @Prop() readonly language: string;
    @Prop() readonly country: string;
    @Prop() readonly disabled: boolean = false;

    /**
     * ex. apc/autocomplete
     */
    @Prop() readonly searcher: string;
    @Prop() readonly maxSuggestions: number = 5;

    /**
     * Maps cose $.groups[].id to proper guided section as in Quick Link Sections prop of the guided component.
     * If no mapping is defined, value is used as-is
     * */
    @Prop() readonly sectionMapping = JSON.stringify({
      'title_autocorrection': 'autocorrection',
      'title_autocomplete': 'autocomplete',
    });
    /**
     * Should we highlight the term in the suggestion?
     */
    @Prop() readonly allowHighlighting: boolean = false;
    @Prop() readonly allowFrontHighlighting: boolean = false;

    @Prop() readonly apikey?: string;
    @Prop() readonly bearer?: string;
    @Prop() readonly tokenUrl?: string;
    @Prop() readonly idmsToken?: string;
    @Prop() readonly apigeeBasicAuth?: string;
    /**
    * When user right-clicks on suggestions, this will be the opened URL
    * term will be substituting $TERM with the search term, urlencoded
    */
    @Prop() readonly suggestionUrlPattern: string = '/$TERM';

    /**
     * Custom Headres object Record<string,string> as json string
     */
    @Prop() readonly customHeaders: string = '{}';

    @Method()
    async fetch(term: string): Promise<Array<GuidedApiSuggestion>> {
        try {
          const url = this.api;

          const authHeaders = await authenticationHeaders({
            apikey: this.apikey,
            bearer: this.bearer,
            tokenUrl: this.tokenUrl,
            idmsToken: this.idmsToken,
            apigeeBasicAuth: this.apigeeBasicAuth,
          });

          const customHeaders = parseJsonWithFallback(this.customHeaders,{},'Failed to parse customHeaders, using default fallback');

          const headers = {...customHeaders, ...authHeaders};


          const response = await axios.post(url, {
            'searcher': this.searcher,
            'country': this.country,
            'language': this.language,
            'term': term,
            'limitPerGroup': this.maxSuggestions
          }, {
            headers: {
              'content-type': 'application/json',
              ...headers
            }
          });

          if (response.status !== 200) {
            err('Guided Universal backend response error:', response);
            return [];
          }
          const json = response.data;

          const r: Array<GuidedApiSuggestion> = [];

          if (json.groups.length == 0)
            return r;

          json.groups.forEach(group => {

              const {id, displayName, documents} = group;

              documents.forEach(item => {

                  let markup = item.userQuery;
                  markup = markup.replace('<span>', '<span class="highlight">');
                  const finalMarkup = this.allowFrontHighlighting ? highlight(term, markup) : markup;

                  const ready = GuidedApiSuggestion.from({
                    sectionId: this.mapSection(id), //sections are ignored for suggestions!
                    markup: finalMarkup,
                    title: item.userQuery,
                    openUrl: this.suggestionUrl(item.userQuery),
                    quartzIcon: 'action_search_stroke',
                    _original: {
                      ...item,
                      _sectionName: displayName,
                    }
                    //chips are taken into account/ But yet no api returns them, lol
                    //description - 2nd line - is not supported by api, lol2
                  });
                  r.push(ready);
              });
          });

          return r;

        } catch (e) {
          console.error('cose guided api error', e); //eslint-disable-line no-console
          return [];
        }
    }

    render(): JSX.Element {
        return (
          <Host/>
        );
    }

    /**
     * Maps section id that comes from cose to usable names, but it's just there for eventual future use
     * Suggestions are not filtered by sections!
     * if no mapping is defined, value is used as-is
     * @param id
     * @private
     */
    private mapSection(id: string) {
        const map = JSON.parse(this.sectionMapping);
        return map[id] || id;
    }

    /**
     * prepares the URL for the suggestion, so the link can be right-clicked
     * @param query
     * @private
     */
    private suggestionUrl(query: string) {
        const term = encodeURIComponent(query);

        return this.suggestionUrlPattern
          .replace('$TERM', term)
          .replace('$LL', this.language.toLowerCase())
          .replace('$CC', this.country.toLowerCase())
        ;
    }
}
